import { Icons } from '@eppendorf/vnls-css/dist';
import { DotsLoader, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './associated-devices-list.module.scss';

export interface AssociatedDevicesListProps {
  loading: boolean;
  showEmptyState?: boolean;
  children?: ReactNode;
  emptyMessage?: string;
  icon?: Icons;
  emptyTitle?: string;
}

function AssociatedDevicesCardRoot({
  className,
  children,
  ...liProps
}: {
  className?: string;
  children: ReactNode;
} & React.LiHTMLAttributes<HTMLLIElement>): ReactNode {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading -- Allow passing through li attributes like key
    <li className={cn(styles.associatedDeviceCard, className)} {...liProps}>
      {children}
    </li>
  );
}

function AssociatedDevicesCardStatus({ children }: { children: ReactNode }): ReactNode {
  return <div className={styles.associatedDeviceCardStatus}>{children}</div>;
}

function AssociatedDeviceCardContent({ children }: { children: ReactNode }): ReactNode {
  return <div className={styles.associatedDeviceCardContent}>{children}</div>;
}

function AssociatedDevicesCardActions({ children }: { children: ReactNode }): ReactNode {
  return <div className={styles.associatedDeviceCardActions}>{children}</div>;
}

export const AssociatedDevicesCard = Object.assign(AssociatedDevicesCardRoot, {
  Status: AssociatedDevicesCardStatus,
  Content: AssociatedDeviceCardContent,
  Actions: AssociatedDevicesCardActions,
});

export function AssociatedDevicesList({
  loading,
  showEmptyState = false,
  children,
  icon = 'not-paired',
  emptyMessage,
  emptyTitle,
}: AssociatedDevicesListProps) {
  const { t } = useTranslation();

  if (loading) {
    return <DotsLoader className="m-top-l" />;
  }

  if (showEmptyState) {
    return (
      <div className={styles.associatedDevicesListEmptyState}>
        <Icon
          name={icon}
          size={IconSizeClasses.Large}
          className={styles.associatedDevicesListEmptyStateIcon}
        />
        <p className={styles.associatedDevicesListEmptyStateTitle}>
          {emptyTitle ?? t('deviceDetail.associatedDevices.emptyTitle')}
        </p>
        <p className={styles.associatedDevicesListEmptyStateMessage}>
          {emptyMessage ?? t('deviceDetail.associatedDevices.emptyMessage')}
        </p>
      </div>
    );
  }

  return <ul className={styles.associatedDevicesList}>{children}</ul>;
}
